import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import {
  User,
  Mail,
  Phone,
  Calendar,
  Building2,
  Trophy,
  Heart,
  MapPin,
  GraduationCap,
  Briefcase,
  CreditCard,
  FileText,
  Eye,
  Pencil,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader, EmptyState } from '@/components/shared'
import { useAuthStore } from '@/store/authStore'
import { employeeService } from '@/services/employeeService'
import { documentService } from '@/services/documentService'
import ProfileForm from '@/components/Profile/ProfileForm'
import { API_BASE_URL } from '@/lib/apiBase'

const Profile = () => {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState('view')

  // Fetch employee profile data
  const { data: profileData, isLoading, refetch } = useQuery(
    'myProfile',
    employeeService.getMyProfile,
    {
      enabled: !!user,
      onError: () => {
        toast.error('Failed to fetch profile data')
      }
    }
  )

  // Fetch documents data
  const { data: documentsData, isLoading: documentsLoading, refetch: refetchDocuments } = useQuery(
    'myDocuments',
    documentService.getMyDocuments,
    {
      enabled: !!user,
      onError: () => {
        toast.error('Failed to fetch documents')
      }
    }
  )

  const employee = profileData?.data?.employee
  const documents = documentsData?.data?.documents || []

  const handleProfileUpdate = () => {
    refetch()
    refetchDocuments()
    setActiveTab('view')
  }

  const handleViewDocument = (document) => {
    window.open(document.view_url, '_blank', 'noopener')
  }

  const getProfilePictureUrl = () => {
    if (!employee?.profile_picture_path) {
      return employee?.profile_picture_url || null
    }
    
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const authData = JSON.parse(authStorage)
        const token = authData.state?.token
        if (token) {
          return `${API_BASE_URL}/employees/profile/picture?token=${encodeURIComponent(token)}`
        }
      } catch (error) {
        console.error('Error parsing auth storage:', error)
      }
    }
    
    return null
  }

  const calculateProfileCompletion = () => {
    if (!employee) return 0
    
    const fields = [
      'contact_number', 'date_of_birth', 'gender', 'marital_status',
      'highest_qualification', 'university', 'graduation_year', 'skills',
      'bank_name', 'account_number', 'ifsc_code', 'pan_number',
      'previous_companies', 'total_experience_years'
    ]
    
    const filledFields = fields.filter(field => employee[field] && employee[field] !== '')
    return Math.round((filledFields.length / fields.length) * 100)
  }

  const InfoItem = ({ icon: Icon, label, value, iconColor = 'text-primary' }) => (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <span className="font-medium">{value || 'Not provided'}</span>
      </div>
    </div>
  )

  const renderProfileView = () => {
    if (isLoading) {
      return (
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <Skeleton className="h-24 w-24 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {/* Header Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={getProfilePictureUrl()} />
                <AvatarFallback className="text-2xl">
                  {employee?.first_name?.charAt(0)}{employee?.last_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <h2 className="text-2xl font-bold">
                  {employee ? `${employee.first_name} ${employee.last_name}` : user?.email}
                </h2>
                {employee && (
                  <>
                    <p className="text-muted-foreground">Employee ID: {employee.employee_id}</p>
                    <Badge variant={employee.status === 'active' ? 'success' : 'secondary'} className={employee.status === 'active' ? 'bg-green-500 mt-2' : 'mt-2'}>
                      {employee.status?.toUpperCase()}
                    </Badge>
                  </>
                )}
              </div>
              
              <div className="text-right space-y-2">
                <div>
                  <p className="text-sm text-muted-foreground">Profile Completion</p>
                  <p className="text-2xl font-bold text-primary">{calculateProfileCompletion()}%</p>
                </div>
                <Progress value={calculateProfileCompletion()} className="w-32" />
                <Button onClick={() => setActiveTab('edit')}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoItem icon={Mail} label="Email" value={user?.email} />
              <InfoItem icon={User} label="Role" value={user?.role?.toUpperCase()} iconColor="text-green-500" />
              {employee && (
                <>
                  <InfoItem icon={Building2} label="Department" value={employee.department} iconColor="text-purple-500" />
                  <InfoItem icon={Trophy} label="Job Title" value={employee.job_title} iconColor="text-orange-500" />
                  <InfoItem 
                    icon={Calendar} 
                    label="Date of Joining" 
                    value={employee.date_of_joining ? dayjs(employee.date_of_joining).format('MMM DD, YYYY') : null}
                    iconColor="text-cyan-500"
                  />
                </>
              )}
              <InfoItem 
                icon={Calendar} 
                label="Member Since" 
                value={dayjs(user?.created_at).format('MMM DD, YYYY')}
                iconColor="text-pink-500"
              />
            </CardContent>
          </Card>

          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-green-500" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {employee ? (
                <>
                  <InfoItem icon={Phone} label="Contact Number" value={employee.contact_number} />
                  <InfoItem 
                    icon={Calendar} 
                    label="Date of Birth" 
                    value={employee.date_of_birth ? dayjs(employee.date_of_birth).format('MMM DD, YYYY') : null}
                    iconColor="text-cyan-500"
                  />
                  <InfoItem icon={User} label="Gender" value={employee.gender} iconColor="text-purple-500" />
                  <InfoItem icon={Heart} label="Marital Status" value={employee.marital_status} iconColor="text-orange-500" />
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Address</p>
                    {employee.addresses && employee.addresses.length > 0 ? (
                      employee.addresses.map((address, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-pink-500 mt-0.5" />
                          <div>
                            <p className="font-medium">
                              {address.address_line_1}
                              {address.address_line_2 && `, ${address.address_line_2}`}
                            </p>
                            <p className="text-muted-foreground">
                              {address.city}, {address.state} - {address.pincode}
                            </p>
                            <Badge variant="outline" className="mt-1 text-xs">
                              {address.address_type === 'residence' ? 'Residence' : 'Current'}
                            </Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-pink-500" />
                        <span className="font-medium">Not provided</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <EmptyState icon="users" title="No profile data" description="Profile information not available" />
              )}
            </CardContent>
          </Card>

          {/* Education */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-purple-500" />
                Education
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {employee ? (
                <>
                  <InfoItem icon={GraduationCap} label="Highest Qualification" value={employee.highest_qualification} />
                  <InfoItem icon={Building2} label="University" value={employee.university} iconColor="text-green-500" />
                  <InfoItem 
                    icon={Calendar} 
                    label="Education Period" 
                    value={
                      employee.education_start_date && employee.education_end_date 
                        ? `${dayjs(employee.education_start_date).format('MMM YYYY')} - ${dayjs(employee.education_end_date).format('MMM YYYY')}`
                        : employee.graduation_year 
                          ? `Graduated in ${employee.graduation_year}`
                          : null
                    }
                    iconColor="text-cyan-500"
                  />
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Skills</p>
                    <p className="font-medium">{employee.skills || 'Not provided'}</p>
                  </div>
                </>
              ) : (
                <EmptyState icon="file" title="No education info" description="Education information not available" />
              )}
            </CardContent>
          </Card>

          {/* Bank Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-500" />
                Bank Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {employee ? (
                <>
                  <InfoItem icon={User} label="Account Holder Name" value={employee.account_holder_name} iconColor="text-green-500" />
                  <InfoItem icon={Building2} label="Bank Name" value={employee.bank_name} />
                  <InfoItem icon={CreditCard} label="Account Number" value={employee.account_number} iconColor="text-purple-500" />
                  <InfoItem icon={CreditCard} label="IFSC Code" value={employee.ifsc_code} iconColor="text-cyan-500" />
                  <InfoItem icon={CreditCard} label="PAN Number" value={employee.pan_number} iconColor="text-orange-500" />
                </>
              ) : (
                <EmptyState icon="file" title="No bank details" description="Bank details not available" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Work Experience */}
        {employee?.workExperiences && employee.workExperiences.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-orange-500" />
                Work Experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {employee.workExperiences.map((exp, index) => (
                  <div key={index} className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-semibold">{exp.position}</h4>
                    <p className="text-primary">{exp.organization_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {dayjs(exp.start_date).format('MMM YYYY')} - {exp.end_date ? dayjs(exp.end_date).format('MMM YYYY') : 'Present'}
                    </p>
                    {exp.location && <p className="text-sm text-muted-foreground">{exp.location}</p>}
                    {exp.description && <p className="text-sm mt-2">{exp.description}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documentsLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </div>
            ) : documents.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {documents.map((doc) => (
                  <Card key={doc.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleViewDocument(doc)}>
                    <CardContent className="p-4 text-center">
                      <FileText className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <p className="font-medium text-sm truncate">
                        {doc.document_type === 'aadhar_card' ? 'Aadhar Card' :
                         doc.document_type === 'pan_card' ? 'PAN Card' :
                         doc.document_type}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{doc.file_name}</p>
                      <p className="text-xs text-muted-foreground">{dayjs(doc.uploaded_at).format('MMM DD, YYYY')}</p>
                      <Button variant="outline" size="sm" className="mt-2">
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <EmptyState icon="document" title="No documents" description="No documents uploaded yet" />
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-muted/50">
            <TabsTrigger value="view" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              <User className="mr-2 h-4 w-4" />
              View Profile
            </TabsTrigger>
            <TabsTrigger value="edit" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              <Pencil className="mr-2 h-4 w-4" />
              Edit Profile
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="view" className="p-6">
            {renderProfileView()}
          </TabsContent>
          
          <TabsContent value="edit" className="p-6">
            <ProfileForm onSuccess={handleProfileUpdate} />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  )
}

export default Profile
